/**
 * NotificationTray
 *
 * A fixed-position slide-in panel to the right of the sidebar.
 * Handles two classes of notification:
 *
 *  1. Standard notifications  — info / success / warning / error
 *  2. Pending-email routing   — type === "pending_email"
 *     Shows an interactive project picker + Assign / Skip actions.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell, CheckCircle, Info, Warning, X, ArrowsClockwise,
  Checks, Trash, EnvelopeSimple, CaretRight, FolderOpen,
} from "@phosphor-icons/react";
import { useNotifications } from "../../contexts/NotificationsContext";
import { getProjects } from "../../api/projects";
import { resolvePending, dismissPending } from "../../api/emailRouting";
import { resolveDrawingRouting, dismissDrawingRouting } from "../../api/drawingRouting";

// ── Shared helpers ─────────────────────────────────────────────────────────────

const TYPE_CONFIG = {
  success: {
    icon:      CheckCircle,
    iconColor: "text-emerald-500",
    border:    "border-l-emerald-400",
    bg:        "bg-emerald-50/60",
  },
  error: {
    icon:      Warning,
    iconColor: "text-red-500",
    border:    "border-l-red-400",
    bg:        "bg-red-50/60",
  },
  warning: {
    icon:      Warning,
    iconColor: "text-amber-500",
    border:    "border-l-amber-400",
    bg:        "bg-amber-50/60",
  },
  info: {
    icon:      Info,
    iconColor: "text-blue-500",
    border:    "border-l-blue-400",
    bg:        "bg-blue-50/40",
  },
  pending_email: {
    icon:      EnvelopeSimple,
    iconColor: "text-violet-500",
    border:    "border-l-violet-400",
    bg:        "bg-violet-50/50",
  },
  pending_drawing: {
    icon:      FolderOpen,
    iconColor: "text-amber-600",
    border:    "border-l-amber-400",
    bg:        "bg-amber-50/50",
  },
  job_complete: {
    icon:      CheckCircle,
    iconColor: "text-green-600",
    border:    "border-l-green-400",
    bg:        "bg-green-50/60",
  },
};

function fmtRelative(iso) {
  const diff = Date.now() - new Date(iso).getTime();
  const m    = Math.floor(diff / 60000);
  if (m < 1)  return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function pct(confidence) {
  return `${Math.round(confidence * 100)}%`;
}

// ── Standard notification row ─────────────────────────────────────────────────

function NotificationRow({ notification, onRead, onDelete, onNavigate }) {
  const { id, title, body, type, read, created_at, link } = notification;
  const cfg  = TYPE_CONFIG[type] || TYPE_CONFIG.info;
  const Icon = cfg.icon;

  const handleClick = () => {
    if (!read) onRead(id);
    if (link)  onNavigate(link);
  };

  return (
    <div
      className={`relative flex gap-3 px-4 py-3.5 border-l-2 transition-colors cursor-pointer group
        ${read
          ? "border-l-transparent bg-white hover:bg-slate-50"
          : `${cfg.border} ${cfg.bg} hover:brightness-95`
        }`}
      onClick={handleClick}
      data-testid={`notification-row-${id}`}
    >
      <Icon
        size={15}
        className={`flex-shrink-0 mt-0.5 ${cfg.iconColor} ${read ? "opacity-50" : ""}`}
        weight="fill"
      />
      <div className="flex-1 min-w-0 pr-6">
        <p className={`text-xs leading-snug ${read ? "font-normal text-slate-600" : "font-semibold text-slate-900"}`}>
          {title}
        </p>
        <p className="text-[11px] text-slate-500 mt-0.5 leading-relaxed line-clamp-2">
          {body}
        </p>
        <p className="text-[10px] text-slate-400 mt-1.5">{fmtRelative(created_at)}</p>
      </div>
      <button
        onClick={(e) => { e.stopPropagation(); onDelete(id); }}
        className="absolute right-3 top-3 opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-500 transition-all p-1 rounded"
        data-testid={`delete-notification-${id}`}
        title="Delete"
      >
        <X size={11} />
      </button>
      {!read && (
        <span className="absolute right-3 bottom-3 w-1.5 h-1.5 rounded-full bg-violet-500" />
      )}
    </div>
  );
}

// ── Interactive pending-drawing row ───────────────────────────────────────────

function PendingDrawingRow({ notification, onResolved, onRead }) {
  const { id, title, created_at, meta = {} } = notification;
  const {
    pending_drawing_id,
    filename      = "unknown file",
    from_email    = "",
    conflict_type = "",
    conflict_detail = "",
    contractor_options = [],
    suggested_type = "",
    version_decision = {},
  } = meta;

  const defaultType = suggested_type || (contractor_options[0]?.type ?? "");
  const [selectedType, setSelectedType] = useState(defaultType);
  const [versionAction, setVersionAction] = useState("");
  const [busy, setBusy]     = useState(false);
  const [done, setDone]     = useState(false);
  const [err,  setErr]      = useState("");
  const [expanded, setExpanded] = useState(true);
  const isVersionReview = conflict_type === "version_ambiguous";

  // Build display options: actual contractor options + "Other" shortcut
  const displayOptions = contractor_options.map((opt) => ({
    value: opt.type,
    label: opt.folder_name || opt.type,
  }));

  const handleConfirm = async () => {
    if (!selectedType || !pending_drawing_id || (isVersionReview && !versionAction)) return;
    setBusy(true);
    setErr("");
    try {
      await resolveDrawingRouting(pending_drawing_id, selectedType, versionAction);
      setDone(true);
      onRead(id);
      setTimeout(() => onResolved(id), 900);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to route drawing. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const handleSkip = async () => {
    if (!pending_drawing_id) return;
    setBusy(true);
    try {
      await dismissDrawingRouting(pending_drawing_id);
      setDone(true);
      onRead(id);
      setTimeout(() => onResolved(id), 600);
    } catch {
      setDone(true);
      setTimeout(() => onResolved(id), 600);
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div
        className="flex items-center gap-2 px-4 py-3 border-l-2 border-l-emerald-400 bg-emerald-50/60"
        data-testid={`pending-drawing-done-${id}`}
      >
        <CheckCircle size={14} className="text-emerald-500 flex-shrink-0" weight="fill" />
        <span className="text-xs text-emerald-700 font-medium">Drawing routed successfully</span>
      </div>
    );
  }

  const isMismatch = conflict_type === "contractor_mismatch";

  return (
    <div
      className="border-l-2 border-l-amber-400 bg-amber-50/50"
      data-testid={`pending-drawing-row-${id}`}
    >
      {/* Header */}
      <div
        className="flex items-start gap-3 px-4 pt-3.5 pb-2 cursor-pointer select-none"
        onClick={() => setExpanded((v) => !v)}
      >
        <FolderOpen
          size={15}
          className="flex-shrink-0 mt-0.5 text-amber-600"
          weight="fill"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-1">
            <p className="text-xs font-semibold text-slate-900 leading-snug truncate">{title}</p>
            <CaretRight
              size={11}
              className={`flex-shrink-0 text-slate-400 transition-transform ${expanded ? "rotate-90" : ""}`}
            />
          </div>
          <p className="text-[11px] text-slate-500 mt-0.5 truncate font-mono">{filename}</p>
          <p className="text-[10px] text-slate-400 mt-0.5">{fmtRelative(created_at)}</p>
        </div>
      </div>

      {expanded && (
        <div className="px-4 pb-3.5 space-y-2.5" onClick={(e) => e.stopPropagation()}>
          {/* Conflict detail */}
          <div className="bg-white rounded border border-amber-100 px-2.5 py-1.5">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-amber-500 mb-0.5">
              {isVersionReview ? "Drawing Version Evidence" : isMismatch ? "Contractor Conflict" : "Unclassified Drawing"}
            </p>
            <p className="text-xs text-slate-600 leading-snug line-clamp-3">{conflict_detail}</p>
            {isVersionReview && version_decision.evidence?.length > 0 && (
              <ul className="mt-1.5 space-y-1 text-[10px] text-slate-500 list-disc pl-4" data-testid={`version-evidence-${id}`}>
                {version_decision.evidence.map((item, index) => <li key={`${index}-${item}`}>{item}</li>)}
              </ul>
            )}
          </div>

          {/* From email */}
          {from_email && (
            <p className="text-[11px] text-slate-500">
              <span className="font-semibold">From:</span> {from_email}
            </p>
          )}

          {!isVersionReview && (
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">
                Place drawing in folder
              </p>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="w-full text-xs border border-slate-200 rounded-md px-2.5 py-1.5 bg-white text-slate-700 focus:outline-none focus:ring-1 focus:ring-amber-400 focus:border-amber-400"
                data-testid={`pending-drawing-folder-select-${id}`}
              >
                <option value="">— Select a contractor folder —</option>
                {displayOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          )}

          {isVersionReview && (
            <div data-testid={`pending-drawing-version-actions-${id}`}>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">
                Choose what to do
              </p>
              <div className="space-y-1">
                {[
                  ["replace_current", "Replace current", "Archive the existing drawing and make this the current revision."],
                  ["archive_incoming", "Keep current", "Archive this incoming drawing as an older revision."],
                  ["treat_as_new", "Separate drawing", "Keep both as unrelated drawings."],
                ].map(([value, label, description]) => (
                  <button
                    type="button"
                    key={value}
                    onClick={() => setVersionAction(value)}
                    aria-pressed={versionAction === value}
                    className={`w-full text-left rounded border px-2.5 py-1.5 transition-colors ${versionAction === value ? "border-amber-500 bg-amber-100" : "border-slate-200 bg-white hover:border-amber-300"}`}
                    data-testid={`version-action-${value}-${id}`}
                  >
                    <span className="block text-[11px] font-semibold text-slate-700">{label}</span>
                    <span className="block text-[10px] text-slate-500">{description}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {err && <p className="text-[11px] text-red-600 leading-snug">{err}</p>}

          {/* Action buttons */}
          <div className="flex gap-2 pt-0.5">
            <button
              onClick={handleConfirm}
              disabled={!selectedType || busy || (isVersionReview && !versionAction)}
              className="flex-1 text-[11px] font-semibold px-3 py-1.5 rounded-md bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-1"
              data-testid={`confirm-drawing-btn-${id}`}
            >
              {busy ? <ArrowsClockwise size={11} className="animate-spin" /> : null}
              {busy ? "Routing…" : isVersionReview ? "Confirm Version Action" : "Place in Folder"}
            </button>
            <button
              onClick={handleSkip}
              disabled={busy}
              className="text-[11px] font-medium px-3 py-1.5 rounded-md border border-slate-200 text-slate-500 hover:text-red-600 hover:border-red-200 disabled:opacity-50 transition-colors"
              data-testid={`skip-drawing-btn-${id}`}
            >
              Skip
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Interactive pending-email row ─────────────────────────────────────────────

function PendingEmailRow({ notification, allProjects, onResolved, onRead }) {
  const { id, title, created_at, meta = {} } = notification;
  const {
    pending_email_id,
    subject    = "(no subject)",
    from_email = "",
    top_matches = [],
    decider_reason = "",
    allow_multiple = false,
  } = meta;

  const [selectedProjectIds, setSelectedProjectIds] = useState([]);
  const [busy, setBusy]   = useState(false);
  const [done, setDone]   = useState(false);
  const [err,  setErr]    = useState("");
  const [expanded, setExpanded] = useState(true);

  // Pre-select the top AI suggestion while allowing the user to add/remove
  // destinations when the email genuinely belongs to multiple projects.
  useEffect(() => {
    if (top_matches.length > 0 && selectedProjectIds.length === 0) {
      setSelectedProjectIds([top_matches[0].project_id]);
    }
  }, [top_matches, selectedProjectIds.length]);

  const toggleProject = (projectId) => {
    if (!projectId) return;
    setSelectedProjectIds((current) => (
      current.includes(projectId)
        ? current.filter((id) => id !== projectId)
        : [...current, projectId]
    ));
  };

  const handleAssign = async () => {
    if (selectedProjectIds.length === 0 || !pending_email_id) return;
    setBusy(true);
    setErr("");
    try {
      await resolvePending(pending_email_id, selectedProjectIds);
      setDone(true);
      onRead(id);
      setTimeout(() => onResolved(id), 900);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to assign email. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const handleSkip = async () => {
    if (!pending_email_id) return;
    setBusy(true);
    try {
      await dismissPending(pending_email_id);
      setDone(true);
      onRead(id);
      setTimeout(() => onResolved(id), 600);
    } catch {
      /* silently dismiss locally */
      setDone(true);
      setTimeout(() => onResolved(id), 600);
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="flex items-center gap-2 px-4 py-3 border-l-2 border-l-emerald-400 bg-emerald-50/60"
           data-testid={`pending-email-done-${id}`}>
        <CheckCircle size={14} className="text-emerald-500 flex-shrink-0" weight="fill" />
        <span className="text-xs text-emerald-700 font-medium">Email routed successfully</span>
      </div>
    );
  }

  return (
    <div
      className="border-l-2 border-l-violet-400 bg-violet-50/50"
      data-testid={`pending-email-row-${id}`}
    >
      {/* Header — click to expand/collapse */}
      <div
        className="flex items-start gap-3 px-4 pt-3.5 pb-2 cursor-pointer select-none"
        onClick={() => setExpanded((v) => !v)}
      >
        <EnvelopeSimple
          size={15}
          className="flex-shrink-0 mt-0.5 text-violet-500"
          weight="fill"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-1">
            <p className="text-xs font-semibold text-slate-900 leading-snug truncate">
              {title}
            </p>
            <CaretRight
              size={11}
              className={`flex-shrink-0 text-slate-400 transition-transform ${expanded ? "rotate-90" : ""}`}
            />
          </div>
          <p className="text-[11px] text-slate-500 mt-0.5 truncate">
            <span className="font-medium">{from_email}</span>
          </p>
          <p className="text-[10px] text-slate-400 mt-0.5">{fmtRelative(created_at)}</p>
        </div>
      </div>

      {/* Expanded body */}
      {expanded && (
        <div className="px-4 pb-3.5 space-y-2.5" onClick={(e) => e.stopPropagation()}>
          {/* Subject pill */}
          <div className="bg-white rounded border border-violet-100 px-2.5 py-1.5">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-violet-400 mb-0.5">Subject</p>
            <p className="text-xs text-slate-700 leading-snug line-clamp-2">{subject}</p>
          </div>

          {decider_reason && (
            <div className="bg-white rounded border border-slate-200 px-2.5 py-1.5" data-testid={`pending-email-decider-reason-${id}`}>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-0.5">Why review is needed</p>
              <p className="text-[11px] text-slate-600 leading-snug">{decider_reason}</p>
            </div>
          )}

          {/* AI suggestions as quick-select chips */}
          {top_matches.length > 0 && (
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">
                AI Suggestions
              </p>
              <div className="flex flex-wrap gap-1">
                {top_matches.map((m) => (
                  <button
                    key={m.project_id}
                    onClick={() => toggleProject(m.project_id)}
                    aria-pressed={selectedProjectIds.includes(m.project_id)}
                    className={`text-[10px] font-medium px-2 py-0.5 rounded-full border transition-all
                      ${selectedProjectIds.includes(m.project_id)
                        ? "bg-violet-600 border-violet-600 text-white"
                        : "bg-white border-slate-200 text-slate-600 hover:border-violet-400 hover:text-violet-700"
                      }`}
                    data-testid={`ai-suggest-${m.project_id}`}
                  >
                    {m.project_name}
                    <span className="ml-1 opacity-70">{pct(m.confidence)}</span>
                    {m.reason && <span className="sr-only"> — {m.reason}</span>}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Add any authorized project, including one outside the AI shortlist */}
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-1">
              {allow_multiple ? "Add one or more projects" : "Choose project destination(s)"}
            </p>
            <select
              value=""
              onChange={(e) => toggleProject(e.target.value)}
              className="w-full text-xs border border-slate-200 rounded-md px-2.5 py-1.5 bg-white text-slate-700 focus:outline-none focus:ring-1 focus:ring-violet-400 focus:border-violet-400"
              data-testid={`pending-email-project-select-${id}`}
            >
              <option value="">— Add a project —</option>
              {allProjects.filter((p) => !selectedProjectIds.includes(p.id)).map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
            {selectedProjectIds.length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-1" data-testid={`pending-email-selected-projects-${id}`}>
                {selectedProjectIds.map((projectId) => {
                  const project = allProjects.find((item) => item.id === projectId);
                  const suggestion = top_matches.find((item) => item.project_id === projectId);
                  return (
                    <button
                      type="button"
                      key={projectId}
                      onClick={() => toggleProject(projectId)}
                      className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 hover:bg-red-50 hover:text-red-700"
                      aria-label={`Remove ${project?.name || suggestion?.project_name || projectId}`}
                    >
                      {project?.name || suggestion?.project_name || projectId} ×
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Error */}
          {err && (
            <p className="text-[11px] text-red-600 leading-snug">{err}</p>
          )}

          {/* Action buttons */}
          <div className="flex gap-2 pt-0.5">
            <button
              onClick={handleAssign}
              disabled={selectedProjectIds.length === 0 || busy}
              className="flex-1 text-[11px] font-semibold px-3 py-1.5 rounded-md bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-1"
              data-testid={`assign-email-btn-${id}`}
            >
              {busy ? <ArrowsClockwise size={11} className="animate-spin" /> : null}
              {busy ? "Routing…" : `Route to ${selectedProjectIds.length} project${selectedProjectIds.length === 1 ? "" : "s"}`}
            </button>
            <button
              onClick={handleSkip}
              disabled={busy}
              className="text-[11px] font-medium px-3 py-1.5 rounded-md border border-slate-200 text-slate-500 hover:text-red-600 hover:border-red-200 disabled:opacity-50 transition-colors"
              data-testid={`skip-email-btn-${id}`}
            >
              Skip
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main tray ─────────────────────────────────────────────────────────────────

export default function NotificationTray() {
  const navigate = useNavigate();
  const {
    trayOpen, setTrayOpen,
    notifications, unreadCount, loading,
    markRead, markAllRead, deleteNotification, clearAll, refresh,
  } = useNotifications();

  const [projects, setProjects] = useState([]);

  // Load projects list whenever tray opens and there are pending_email notifications
  const hasPendingEmail = notifications.some((n) => n.type === "pending_email");  useEffect(() => {
    if (!trayOpen || !hasPendingEmail) return;
    getProjects()
      .then(setProjects)
      .catch(() => {});
  }, [trayOpen, hasPendingEmail]);

  const handleNavigate = (link) => {
    setTrayOpen(false);
    navigate(link);
  };

  // When a pending email is resolved/dismissed, remove it and refresh unread count
  const handleResolved = useCallback((notifId) => {
    deleteNotification(notifId);
  }, [deleteNotification]);

  return (
    <>
      {/* Backdrop */}
      {trayOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/20 backdrop-blur-[1px]"
          onClick={() => setTrayOpen(false)}
          data-testid="notification-backdrop"
        />
      )}

      {/* Tray panel */}
      <div
        className={`
          fixed top-0 bottom-0 left-0 w-[800px] z-30 bg-white
          border-r border-slate-200 shadow-2xl flex flex-col
          transition-transform duration-300 ease-in-out
          ${trayOpen ? "translate-x-64 pointer-events-auto" : "-translate-x-full pointer-events-none"}
        `}
        data-testid="notification-tray"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-4 border-b border-slate-100 flex-shrink-0">
          <div className="flex items-center gap-2">
            <Bell size={14} className="text-slate-600" weight="fill" />
            <h2 className="text-sm font-bold text-slate-800">Notifications</h2>
            {unreadCount > 0 && (
              <span className="inline-flex items-center justify-center w-5 h-5 text-[10px] font-bold rounded-full bg-violet-600 text-white">
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </div>
          <button
            onClick={() => setTrayOpen(false)}
            className="text-slate-400 hover:text-slate-700 p-1 rounded transition-colors"
            data-testid="close-notification-tray-btn"
          >
            <X size={14} />
          </button>
        </div>

        {/* Action row */}
        {notifications.length > 0 && (
          <div className="flex items-center justify-between px-4 py-2 border-b border-slate-100 bg-slate-50/60 flex-shrink-0">
            <button
              onClick={markAllRead}
              disabled={unreadCount === 0}
              className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500 hover:text-violet-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              data-testid="mark-all-read-btn"
            >
              <Checks size={11} />
              Mark all read
            </button>
            <button
              onClick={clearAll}
              className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500 hover:text-red-600 transition-colors"
              data-testid="clear-all-notifications-btn"
            >
              <Trash size={11} />
              Clear all
            </button>
          </div>
        )}

        {/* Notification list */}
        <div className="flex-1 overflow-y-auto divide-y divide-slate-100">
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-12 text-slate-400"
                 data-testid="notifications-loading">
              <ArrowsClockwise size={15} className="animate-spin" />
              <span className="text-xs">Loading…</span>
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 px-6 text-center"
                 data-testid="notifications-empty">
              <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mb-3">
                <Bell size={20} className="text-slate-400" />
              </div>
              <p className="text-sm font-medium text-slate-600">No notifications</p>
              <p className="text-xs text-slate-400 mt-1">
                Platform alerts and updates will appear here.
              </p>
            </div>
          ) : (
            notifications.map((n) =>
              n.type === "pending_email" ? (
                <PendingEmailRow
                  key={n.id}
                  notification={n}
                  allProjects={projects}
                  onResolved={handleResolved}
                  onRead={markRead}
                />
              ) : n.type === "pending_drawing" ? (
                <PendingDrawingRow
                  key={n.id}
                  notification={n}
                  onResolved={handleResolved}
                  onRead={markRead}
                />
              ) : (
                <NotificationRow
                  key={n.id}
                  notification={n}
                  onRead={markRead}
                  onDelete={deleteNotification}
                  onNavigate={handleNavigate}
                />
              )
            )
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-slate-100 bg-slate-50/60 flex-shrink-0">
          <p className="text-[10px] text-slate-400 text-center">
            {notifications.length} notification{notifications.length !== 1 ? "s" : ""}
            {unreadCount > 0 && ` · ${unreadCount} unread`}
          </p>
        </div>
      </div>
    </>
  );
}
